import React, { useMemo, useState } from 'react'
import useTrans from '@hooks/useTrans'
import Datatable, {
  DatatableRow,
  DatatableCell,
  DatatableCellPrice,
} from '@components/atoms/Datatable/Datatable'
import { PlusCircle, MinusCircle } from '@components/icons'
import styles from './DatatableOrdiniDetail.module.sass'
import MyAccountArticoloOrdine from '../MyAccountArticoloOrdine/MyAccountArticoloOrdine'
import { RigaOrdinePersonalizzazioneType, RootStateType, UserType } from '@utils/types'
import { cn, priceValutaNotation } from '@utils/safeTs'
import { useSelector } from 'react-redux'


interface DatatableOrdiniDetailAccordionRigaOrdineProps {
  open: boolean
  rigaOrdine: RigaOrdinePersonalizzazioneType
  hideNote?: boolean
}

interface DatatableOrdiniDetailProps {
  righeOrdine?: RigaOrdinePersonalizzazioneType[]
  userType?: UserType
  variant?: '' | 'caricamento-bozze'
}

const AccordionRigaOrdine = (props: DatatableOrdiniDetailAccordionRigaOrdineProps) => {

  const { 
    open, 
    rigaOrdine, 
    hideNote = false
  } = props

  const t = useTrans()
  const { service_gift } = useSelector((state: RootStateType) => state.servicegift)
  const prezzoMerceNeutra = useMemo(() => service_gift ? rigaOrdine.totale : rigaOrdine.prezzoMerceNeutra, [rigaOrdine, service_gift])

  return open ? (
    <div className={styles.extra}>
      <div className={styles.extraNote}>
        {!hideNote && (
          <p>
            <strong>
              {t("Note:")}{' '}
            </strong>
            {rigaOrdine.nota || '-'}
          </p>
        )}
      </div>
      <div className={styles.extraInfo}>
        {prezzoMerceNeutra > 0 && (
          <div className={styles.extraInfoRow}>
            <p>{t("Valore merce")}</p>
            <p>{priceValutaNotation(prezzoMerceNeutra, 3)}</p>
          </div>
        )}

        {rigaOrdine?.prezzoPersonalizzazione > 0 && (
          <div className={styles.extraInfoRow}>
            <p>{t("Valore personalizzazione")}</p>
            <p>{priceValutaNotation(rigaOrdine.prezzoPersonalizzazione, 3)}</p>
          </div>
        )}

        {rigaOrdine?.prezzoConfezionamento > 0 && (
          <div className={styles.extraInfoRow}>
            <p>{t("Costo confezionamento")}</p>
            <p>{priceValutaNotation(rigaOrdine.prezzoConfezionamento * (service_gift ? rigaOrdine.quantita : 1), 3)}</p>
          </div>
        )}
      </div>
    </div>
  ) : (
    <></>
  )
}

const DatatableOrdiniDetail = (props: DatatableOrdiniDetailProps) => {

  const { righeOrdine, userType, variant = '' } = props

  const t = useTrans()
  const [open, setOpen] = useState(false)
  const { service_gift } = useSelector((state: RootStateType) => state.servicegift)


  const cols = {
    prodotto: { key: 'prodotto', label: t('Prodotto') }, // non deve andare a capo - largh fissa
    quantita: { key: 'quantita', label: t('Quantità'), desktopAlign: 'right' },
    ...service_gift ? {} : { 
      listino: { key: 'listino', label: t('Listino'), desktopAlign: 'right' },
      sconto: { key: 'sconto', label: t('Sconto'), desktopAlign: 'right' },
    },
    prezzo: { key: 'prezzo', label: t('Prezzo'), desktopAlign: 'right' },
    totale: { key: 'totale', label: t('Totale'), desktopAlign: 'right' },
    openTrigger: {
      key: 'openTrigger',
      label: (
        <button type="button" onClick={() => setOpen(!open)} className={styles.detailTrigger}>
          {open ? <MinusCircle aria-hidden /> : <PlusCircle aria-hidden />}
        </button>
      ),
      mobileHidden: true,
      width: '36px',
    },
  }

  return (
    <Datatable cols={cols} className={cn("datatable-ordini", styles.root, styles[`root--${userType}`])}>
      {righeOrdine?.map((rigaOrdine, index) => (
        <DatatableRow key={index} extra={<AccordionRigaOrdine open={open} rigaOrdine={rigaOrdine} hideNote={variant === 'caricamento-bozze'} />}>
          <DatatableCell col={cols.prodotto}>
            <MyAccountArticoloOrdine 
              articolo={rigaOrdine.articolo} 
              nota={variant === 'caricamento-bozze' ? rigaOrdine.nota : undefined}
            />
          </DatatableCell>
          <DatatableCell col={cols.quantita}>{rigaOrdine.quantita || ''}</DatatableCell>
          <DatatableCellPrice col={cols.listino} value={rigaOrdine.prezzoListino ?? ''} />
          <DatatableCell col={cols.sconto}>
            {rigaOrdine?.percentualeSconto > 0 && `${rigaOrdine.percentualeSconto} %`}
          </DatatableCell>
          <DatatableCellPrice col={cols.prezzo} value={rigaOrdine.prezzoUnitarioNetto ?? ''} />
          <DatatableCellPrice
            col={cols.totale}
            value={rigaOrdine?.totale}
          />
          <DatatableCell col={cols.openTrigger}></DatatableCell>
        </DatatableRow>
      ))}
    </Datatable>
  )
}

export default DatatableOrdiniDetail